// Row-limiting clause emission — the shared answer at every caret from the
// `LIMIT` / `OFFSET` / `FETCH` keyword to the end of the statement.
//
// That whole region takes integers and the clause's own keywords: a table, a
// column, a routine or the general keyword list is a syntax error at any of
// those carets. Before this existed every engine fell through to its full
// emission and offered 300–500 candidates (the user-reported Oracle `FETCH ▮`
// flood was one caret of the same bug).
//
// The answer is therefore: this slot's keywords (see `ROW_LIMIT_SLOTS` in
// `legalNextKeywords.ts` for why some of those are a clause-shape hint rather
// than a token legal at the caret this instant) plus the integer-valued
// operands already in scope — procedural locals and routine parameters, which
// is how `LIMIT p_limit` inside a function body completes. Everything else is
// dropped, including when this leaves the list empty: MySQL's `LIMIT ▮` takes a
// bare integer and nothing else, and closing the widget is the right answer.

import type { CompletionCandidate, DatabaseEngineId } from '@/core/engines';

import { getLegalNextKeywords, type NextKeywordSlot } from '../context/legalNextKeywords';

/** Candidate kinds that can stand where the clause wants an integer: PL/SQL,
 *  PL/pgSQL, T-SQL and MySQL routine locals plus routine parameters. Everything
 *  the resolvers emit for these carries one of these two kinds. */
const OPERAND_KINDS: ReadonlySet<CompletionCandidate['kind']> = new Set<CompletionCandidate['kind']>([
  'variable',
  'parameter',
]);

/**
 * Builds the completion list at a row-limiting slot from the candidates emitted
 * so far. Callers pass the resolver's in-progress `out` array and return the
 * result directly — the schema emission that would normally follow must NOT run.
 */
export function buildRowLimitOperandCandidates(
  emittedSoFar: readonly CompletionCandidate[],
  slot: NextKeywordSlot,
  engine: DatabaseEngineId,
): CompletionCandidate[] {
  const out: CompletionCandidate[] = emittedSoFar.filter((c) => OPERAND_KINDS.has(c.kind));
  getLegalNextKeywords(engine, slot).forEach((kw, i) => {
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'SQL keyword',
      orderHint: i,
    });
  });
  return out;
}
